import React from 'react';
import styled from 'styled-components';
import * as size from '@constant/size';
import * as color from '@constant/color';
import TextBlock from '@component/common/block/text-block';
import Gap from '@component/common/gap';
import { actions as modalActions } from '@store/slice/modal-slice';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { MEDIUM_MODAL_WIDTH } from '@constant/size';
import { IRemoveConfirmModalData } from '@datatype/iyagigo/type';
import {
  useModifyClozeWorkbookMutation,
  useModifyVocabularyWorkbookMutation,
  useBookmarkClozeWorkbookQuery,
  useIncorrectNoteClozeWorkbookQuery,
  useVocabularyWorkbookBookmarkPageQuery,
  useVocabularyWorkbookIncorrectNotePageQuery,
} from '@hook/iyagigo/useApi';
import { getClozeTypeBySubject } from '@util/workbook';
import { EApiLanguageCode } from '@datatype/iyagigo/api-type';
import TextButton from '@design-system/text-button';
import { ETheme } from '@datatype/type';

interface IProps {
  data: IRemoveConfirmModalData['data'];
}

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: ${MEDIUM_MODAL_WIDTH};
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  gap: ${size.MARGIN_1};
  width: 100%;
`;

const DIALOG_TEXT_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: false },
} as const;

const RemoveConfirmModal: React.FC<IProps> = ({ data }) => {
  const dispatch = useTypedDispatch();
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const todaySubMenu = useTypedSelector((state) => state.rootReducer?.navigationReducer?.iyagigo?.subMenu) ?? 'vocabulary';

  const isVocabularyBook = data.domain === 'vocabulary-book';
  const isIncorrect = data.isIncorrect;
  const languageCode =
    studyLanguage === EApiLanguageCode.ENGLISH ? EApiLanguageCode.ENGLISH : EApiLanguageCode.JAPANESE;

  // cloze
  const clozeMutation = useModifyClozeWorkbookMutation(
    getClozeTypeBySubject(todaySubMenu),
    languageCode,
  );
  const clozeBookmarkQuery = useBookmarkClozeWorkbookQuery(
    getClozeTypeBySubject(todaySubMenu),
    languageCode,
  );
  const clozeIncorrectNoteQuery = useIncorrectNoteClozeWorkbookQuery(
    getClozeTypeBySubject(todaySubMenu),
    languageCode,
  );

  // vocabulary-book
  const vocaMutation = useModifyVocabularyWorkbookMutation(languageCode, null, null, null);
  const vocaBookmarkQuery = useVocabularyWorkbookBookmarkPageQuery(languageCode);
  const vocaIncorrectNoteQuery = useVocabularyWorkbookIncorrectNotePageQuery(languageCode);

  const handleConfirmClick = async () => {
    if (isVocabularyBook) {
      if (isIncorrect) {
        await vocaMutation.mutateAsync({ workbookKey: data.workbookKey, isIncorrectNote: false });
        vocaIncorrectNoteQuery.refetch();
      } else {
        await vocaMutation.mutateAsync({ workbookKey: data.workbookKey, isBookmark: false });
        vocaBookmarkQuery.refetch();
      }
    } else {
      if (isIncorrect) {
        await clozeMutation.mutateAsync({ workbookKey: data.workbookKey, isIncorrectNote: false });
        clozeIncorrectNoteQuery.refetch();
      } else {
        await clozeMutation.mutateAsync({ workbookKey: data.workbookKey, isBookmark: false });
        clozeBookmarkQuery.refetch();
      }
    }
    dispatch(modalActions.hideModal());
  };

  const handleCancelClick = () => {
    dispatch(modalActions.hideModal());
  };

  const confirmText = (() => {
    if (isVocabularyBook) {
      return isIncorrect ? '틀린 단어를 삭제하시겠습니까?' : '저장한 단어를 삭제하시겠습니까?';
    }
    return isIncorrect ? '틀린 문제를 삭제하시겠습니까?' : '저장한 문제를 삭제하시겠습니까?';
  })();

  return (
    <Wrapper>
      <TextBlock text={confirmText} design={DIALOG_TEXT_DESIGN} />
      <Gap height={size.MARGIN_2} />
      <ButtonContainer>
        <TextButton text='취소' theme={ETheme.GRAY} size='medium' shape='pill' width='100%' handleClick={handleCancelClick} />
        <TextButton text='삭제' theme={ETheme.BROWN} size='medium' shape='pill' width='100%' handleClick={handleConfirmClick} />
      </ButtonContainer>
    </Wrapper>
  );
};

export default RemoveConfirmModal;
